import React, { useState, useRef } from "react"
import i18n from "i18n"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import PropTypes from "prop-types"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import Popover from "@mui/material/Popover"
import { SketchPicker } from "react-color"
import { useUtils } from "hooks/useUtils"

const ModalGeneralInfoSettingsPdfTab = ({
  userInfo,
  setUserInfo,
  showColorSecondary = true,
}) => {
  const [anchorPicker1, setAnchorPicker1] = useState(null)
  const [anchorPicker2, setAnchorPicker2] = useState(null)
  const fileInputRef = useRef(null)
  const { generateFileUrl, uploading } = useUtils()

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ fontSize: 14, color: "#414141", mb: 1 }}>
            {i18n.t("generalInfoSettings.logo")}
          </Box>
          {uploading ? (
            <Box
              sx={{
                border: "2px dashed #aaa",
                borderRadius: 8,
                p: 2,
                textAlign: "center",
                background: "#fafafa",
                minHeight: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: "#888",
                fontSize: 16,
              }}
            >
              {i18n.t("generalInfoSettings.uploading")}
            </Box>
          ) : userInfo.logo ? (
            <Box
              sx={{
                position: "relative",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                background: "#fafafa",
                minHeight: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={userInfo.logo}
                alt={i18n.t("generalInfoSettings.logo")}
                style={{
                  width: "100%",
                  height: 120,
                  objectFit: "contain",
                  borderRadius: 4,
                  display: "block",
                }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  background: "rgba(255,255,255,0.8)",
                }}
                onClick={() => setUserInfo(prev => ({ ...prev, logo: null }))}
                aria-label={i18n.t("generalInfoSettings.removeLogo")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                border: "2px dashed #aaa",
                borderRadius: 8,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                background: "#fafafa",
                minHeight: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
              onDragOver={e => e.preventDefault()}
              onDrop={async e => {
                e.preventDefault()
                const file = e.dataTransfer.files[0]
                if (file && file.type.startsWith("image/")) {
                  const url = await generateFileUrl(file, "protected")
                  if (url) {
                    setUserInfo(prev => ({ ...prev, logo: url }))
                  }
                }
              }}
            >
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={async e => {
                  const file = e.target.files[0]
                  if (file && file.type.startsWith("image/")) {
                    const url = await generateFileUrl(file, "protected")
                    if (url) {
                      setUserInfo(prev => ({ ...prev, logo: url }))
                    }
                  }
                }}
              />
              <Box
                sx={{ cursor: "pointer", color: "#888", textWrap: "balance" }}
              >
                {i18n.t("generalInfoSettings.clickOrDragToUpload")}
              </Box>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ fontSize: 14, color: "#414141", mb: 1 }}>
              {i18n.t("generalInfoSettings.colorPrimary")}
            </Box>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                onClick={e =>
                  setAnchorPicker1(prev => (prev ? null : e.currentTarget))
                }
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 6,
                  backgroundColor: userInfo.colorPrimary || "#C4C4C4",
                  border: "1px solid #ddd",
                  cursor: "pointer",
                }}
              />
              <TextField
                size="small"
                variant="outlined"
                value={userInfo.colorPrimary || ""}
                onChange={e =>
                  setUserInfo(prev => ({
                    ...prev,
                    colorPrimary: e.target.value,
                  }))
                }
                placeholder="#RRGGBB"
              />
            </div>
            <Popover
              open={Boolean(anchorPicker1)}
              anchorEl={anchorPicker1}
              onClose={() => setAnchorPicker1(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <div style={{ padding: 8 }}>
                <SketchPicker
                  color={userInfo.colorPrimary || "#ffffff"}
                  onChangeComplete={color =>
                    setUserInfo(prev => ({ ...prev, colorPrimary: color.hex }))
                  }
                  disableAlpha={true}
                />
              </div>
            </Popover>
          </Box>
          {showColorSecondary && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ fontSize: 14, color: "#414141", mb: 1 }}>
                {i18n.t("generalInfoSettings.colorSecondary")}
              </Box>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  onClick={e =>
                    setAnchorPicker2(prev => (prev ? null : e.currentTarget))
                  }
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    backgroundColor: userInfo.colorSecondary || "#C4C4C4",
                    border: "1px solid #ddd",
                    cursor: "pointer",
                  }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  value={userInfo.colorSecondary || ""}
                  onChange={e =>
                    setUserInfo(prev => ({
                      ...prev,
                      colorSecondary: e.target.value,
                    }))
                  }
                  placeholder="#RRGGBB"
                />
              </div>
              <Popover
                open={Boolean(anchorPicker2)}
                anchorEl={anchorPicker2}
                onClose={() => setAnchorPicker2(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <div style={{ padding: 8 }}>
                  <SketchPicker
                    color={userInfo.colorSecondary || "#ffffff"}
                    onChangeComplete={color =>
                      setUserInfo(prev => ({
                        ...prev,
                        colorSecondary: color.hex,
                      }))
                    }
                    disableAlpha={true}
                  />
                </div>
              </Popover>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

ModalGeneralInfoSettingsPdfTab.propTypes = {
  userInfo: PropTypes.object.isRequired,
  setUserInfo: PropTypes.func.isRequired,
  showColorSecondary: PropTypes.bool,
}

export default ModalGeneralInfoSettingsPdfTab
